import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Button, Form, Stack } from 'react-bootstrap';
import SearchJobs from 'components/Common/Jobs/SearchJobs';
import ReactSelect from 'react-select';
import { useSearchParams } from 'react-router-dom';
import { useJobListFilterQuery, useJobListMutation } from 'services';
import useTranslate from 'hooks/useTranslate';
import PaginationBox from 'components/PaginationBox';
import { dataLimits, USER_TYPES } from 'constants';
import JobListingBox from 'components/Common/Jobs/JobListingBox';
import useAuthStore from 'store/auth';
import useCommonStore from 'store/common';
import './styles.scss';
import useLocalizationStore from 'store/localization';

const FilteredJobs: React.FC = () => {
	const { translate } = useTranslate();
	const salaryRef = useRef<any>(null);
	const limit = dataLimits?.filterJobs || 10;
	const authUser: any = useAuthStore((state) => state.userInfo.user);
	const { activeLanguage }: any = useLocalizationStore((state) => state);
	const { mutateAsync: filterJobs } = useJobListMutation();
	const [jobList, setJobList] = useState([]);
	const [totalData, setTotalData] = useState(0);

	const [currentPage, setCurrentPage] = useState(1);
	const { setModalConfig, hideCommonModal } = useCommonStore((state) => state);

	const {
		data: {
			companyType: companyTypeArr = [],
			jobPosted: jobPostedArr = [],
			jobType: jobTypeArr = [],
			levels: levelsArr = [],
			salary: salaryArr = [],
			workspace: workspaceArr = [],
		} = {},
	} = useJobListFilterQuery();

	const salaryTypeArr = Object.keys(salaryArr).map((key) => ({
		value: key,
	}));

	const [searchParams, setSearchParams] = useSearchParams();
	const paramsObject = Object.fromEntries(searchParams.entries());
	const {
		jobType = '',
		salaryType = '',
		salary = '',
		levelId = '',
		workspace = '',
		companyType = '',
		jobPosted = '',
	} = paramsObject;

	const [searchString, setSearchString] = useState(
		Object.keys(paramsObject)?.length
			? ''
			: `page=${currentPage}&limit=${limit}&order=DESC&hasExpired=false&inActive=false&type=all`,
	);
	const onHandleChange = (key: string, value: any) => {
		setCurrentPage(1);
		const updatedParams = { ...paramsObject };

		if (value !== undefined) {
			updatedParams[key] = value || '';
		} else {
			delete updatedParams[key];
		}

		if (key === 'salaryType') {
			salaryRef?.current?.clearValue();
			if (value === undefined) {
				delete updatedParams.salary;
			} else {
				updatedParams.salary = '';
			}
		}

		setSearchParams(updatedParams);
	};

	useEffect(() => {
		if (Object.keys(paramsObject)?.length || searchString) {
			const otherPayload = [
				`page=${currentPage}`,
				`limit=${limit}`,
				'order=DESC&hasExpired=false&inActive=false&type=all',
			].join('&');
			const queryOptions = Object.keys(paramsObject)
				.map((key) => `${key}=${encodeURIComponent(paramsObject[key] ?? '')}`)
				.join('&');
			setSearchString(`${queryOptions}${queryOptions ? `&${otherPayload}` : otherPayload}`);
		}
	}, [paramsObject, currentPage]);

	useEffect(() => {
		if (searchString) {
			filterJobs(searchString).then((res: any) => {
				if (res?.success) {
					const jobData = res?.data;
					setJobList(jobData?.docs || []);
					const { totalDocs = 0 } = jobData?.meta || {};
					setTotalData(totalDocs);
				}
			});
		}
	}, [searchString, activeLanguage]);

	const onShowModal = (type: string, id: number | null = null) => {
		setModalConfig({
			visible: true,
			onClose: hideCommonModal,
			id,
			type,
		});
	};

	const handleSavedJob = (id: number | null = null) => {
		if (!authUser?.userId) {
			setModalConfig({
				visible: true,
				id,
				type: 'loginModal',
			});
			return;
		}
		onShowModal('saveJobAlert');
	};

	return (
		<section className="filtered-jobs py-50">
			<Container fluid>
				<div className="white-box bg-white mb-4">
					<Container>
						<div className="banner-filters py-2">
							<SearchJobs />
						</div>
					</Container>
				</div>
				<div className="filtered-jobs-filters d-flex flex-column flex-xl-row align-items-xl-center">
					<h5 className="mb-0">{translate('Common.filters')}</h5>
					<div className="filters-list d-flex flex-wrap">
						<Form.Group className="position-relative">
							<ReactSelect
								isSearchable
								isClearable
								name="jobType"
								options={salaryTypeArr}
								getOptionValue={(option) => option.value}
								getOptionLabel={(option) => option.value}
								onChange={(optionValue: any) => onHandleChange('salaryType', optionValue?.value)}
								autoFocus={false}
								placeholder={translate('PlaceHolder.salaryType')}
								className="form-react-select "
								classNamePrefix="select"
								value={salaryTypeArr?.find((item: any) => item.value === salaryType)}
								isOptionDisabled={(item: any) => item.value === salaryType}
							/>
						</Form.Group>
						{Object.keys(salaryArr)?.includes(salaryType) && (
							<Form.Group className="position-relative">
								<ReactSelect
									isSearchable
									isClearable
									name="salary"
									options={salaryArr[salaryType] || []}
									getOptionValue={(option) => option.id}
									getOptionLabel={(option) => option.value}
									onChange={(optionValue: any) => onHandleChange('salary', optionValue?.id)}
									autoFocus={false}
									placeholder={translate('PlaceHolder.salaryRange')}
									className="form-react-select "
									classNamePrefix="select"
									value={salaryArr[salaryType]?.find((item: any) => item.id === Number(salary))}
									isOptionDisabled={(item: any) => item.id === Number(salary)}
									ref={salaryRef}
								/>
							</Form.Group>
						)}

						<Form.Group className="position-relative">
							<ReactSelect
								isSearchable
								isClearable
								name="jobType"
								options={jobTypeArr}
								getOptionValue={(option) => option.type}
								getOptionLabel={(option) => option.type}
								onChange={(optionValue: any) => onHandleChange('jobType', optionValue?.type)}
								autoFocus={false}
								placeholder={translate('PlaceHolder.jobType')}
								className="form-react-select "
								classNamePrefix="select"
								value={jobTypeArr?.find((item: any) => item.type === jobType)}
								isOptionDisabled={(item: any) => item.type === jobType}
							/>
						</Form.Group>
						<Form.Group className="position-relative">
							<ReactSelect
								isSearchable
								isClearable
								name="levelId"
								options={levelsArr}
								getOptionValue={(option) => option.id}
								getOptionLabel={(option) => option.title}
								onChange={(optionValue: any) => onHandleChange('levelId', optionValue?.id)}
								autoFocus={false}
								placeholder={translate('PlaceHolder.experienceLevel')}
								className="form-react-select "
								classNamePrefix="select"
								value={levelsArr?.find((item: any) => item.id === Number(levelId))}
								isOptionDisabled={(item: any) => item.id === Number(levelId)}
							/>
						</Form.Group>
						<Form.Group className="position-relative">
							<ReactSelect
								isSearchable
								isClearable
								name="workspace"
								options={workspaceArr}
								getOptionValue={(option) => option.value}
								getOptionLabel={(option) => option.title}
								onChange={(optionValue: any) => onHandleChange('workspace', optionValue?.value)}
								autoFocus={false}
								placeholder={translate('PlaceHolder.remote')}
								className="form-react-select "
								classNamePrefix="select"
								value={workspaceArr?.find((item: any) => item.value === workspace)}
								isOptionDisabled={(item: any) => item.value === workspace}
							/>
						</Form.Group>
						<Form.Group className="position-relative">
							<ReactSelect
								isSearchable
								isClearable
								name="companyType"
								options={companyTypeArr}
								getOptionValue={(option) => option.type}
								getOptionLabel={(option) => option.type}
								onChange={(optionValue: any) => onHandleChange('companyType', optionValue?.type)}
								autoFocus={false}
								placeholder={translate('PlaceHolder.companyType')}
								className="form-react-select "
								classNamePrefix="select"
								value={companyTypeArr?.find((item: any) => item.type === companyType)}
								isOptionDisabled={(item: any) => item.type === companyType}
							/>
						</Form.Group>
						<Form.Group className="position-relative">
							<ReactSelect
								isSearchable
								isClearable
								name="jobPosted"
								options={jobPostedArr}
								getOptionValue={(option) => option.value}
								getOptionLabel={(option) => option.label}
								onChange={(optionValue: any) => onHandleChange('jobPosted', optionValue?.value)}
								autoFocus={false}
								placeholder={translate('PlaceHolder.datePosted')}
								className="form-react-select "
								classNamePrefix="select"
								value={jobPostedArr?.find(
									(item: any) => item.value == decodeURIComponent(jobPosted),
								)}
								isOptionDisabled={(item: any) => item.value == decodeURIComponent(jobPosted)}
							/>
						</Form.Group>
					</div>
				</div>
				<div className="white-box bg-white">
					<div className="jobs-filter d-flex align-items-center justify-content-between mb-3 gap-3">
						<p className="fw-semibold mb-0"> {totalData} Jobs</p>
						{authUser?.userType === USER_TYPES.SEEKER && (
							<Button
								className="transparent-btn rounded-pill lh-1"
								onClick={() => handleSavedJob()}
							>
								{translate('Common.saveJobAlert')}
							</Button>
						)}
					</div>

					<div className="filtered-jobs-list">
						<Row className="filtered-jobs-list-row">
							{jobList?.length > 0 ? (
								jobList?.map((job: any) => (
									<Col key={`${job.jobId}-${job.title}-${job.companyId}`} xl={6}>
										<JobListingBox
											job={job}
											key={`${job.jobId}-${job.title}--list-${job.companyId}`}
											enableApplyBtn={false}
											enableAddress={false}
										/>
									</Col>
								))
							) : (
								<Stack className="posted-job-box">
									<p className="mb-0">{translate('Common.noJobsFound')} </p>
								</Stack>
							)}
						</Row>
						{totalData > limit && (
							<PaginationBox
								totalData={totalData}
								limit={limit}
								page={currentPage}
								setCurrentPage={setCurrentPage}
							/>
						)}
					</div>
				</div>
			</Container>
		</section>
	);
};

export default FilteredJobs;
